import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  Action,
  AnswerType,
  Answers,
  Master,
  Variables,
  masterValidation,
} from '../code';
import { WorkService } from '../services';
import { Observable, merge, of } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SubSink } from 'subsink';
import { shareReplayRefCount, valuesOf } from '@adreno-common';
import { formatDate } from '@angular/common';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-information',
  templateUrl: './information.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InformationComponent implements OnInit {
  private subs = new SubSink();
  fgMaster$!: Observable<FormGroup>;
  fgQField$!: Observable<FormGroup>;
  viewModel$!: Observable<ViewModel>;

  constructor(
    private workSvc: WorkService,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {}

  private initFormGroup() {
    this.fgMaster$ = this.workSvc.master$.pipe(
      take(1),
      map((master) => {
        let localISO: string | null = null;
        if (master?.u2) {
          //const u2adj = new Date(
          //  master.u2.valueOf() - master.u2.getTimezoneOffset() * 60 * 1000
          //);
          localISO = formatDate(master.u2, 'yyyy-MM-ddTHH:mm:ss.SSS', 'en');
        }
        return this.fb.group({
          u1: [
            master?.u1,
            Validators.maxLength(masterValidation.u1!.maxLength),
          ],
          u3: [
            master?.u3,
            Validators.maxLength(masterValidation.u3!.maxLength),
          ],
          u4: [
            master?.u4,
            Validators.maxLength(masterValidation.u4!.maxLength),
          ],
          u5: [
            master?.u5,
            Validators.maxLength(masterValidation.u5!.maxLength),
          ],
          u6: [
            master?.u6,
            Validators.maxLength(masterValidation.u6!.maxLength),
          ],
          u7: [
            master?.u7,
            Validators.maxLength(masterValidation.u7!.maxLength),
          ],
          u8: [
            master?.u8,
            Validators.maxLength(masterValidation.u8!.maxLength),
          ],
          u9: [
            master?.u9,
            Validators.maxLength(masterValidation.u9!.maxLength),
          ],
          u10: [
            master?.u10,
            Validators.maxLength(masterValidation.u10!.maxLength),
          ],
          u11: [
            master?.u11,
            Validators.maxLength(masterValidation.u11!.maxLength),
          ],
          u12: [
            master?.u12,
            Validators.maxLength(masterValidation.u12!.maxLength),
          ],
          u13: [
            master?.u13,
            Validators.maxLength(masterValidation.u13!.maxLength),
          ],
          u14: [
            master?.u14,
            Validators.maxLength(masterValidation.u14!.maxLength),
          ],
          u15: [
            master?.u15,
            Validators.maxLength(masterValidation.u15!.maxLength),
          ],
          u16: [
            master?.u16,
            Validators.maxLength(masterValidation.u16!.maxLength),
          ],
          u17: [
            master?.u17,
            Validators.maxLength(masterValidation.u17!.maxLength),
          ],
          u18: [
            master?.u18,
            Validators.maxLength(masterValidation.u18!.maxLength),
          ],
          u19: [
            master?.u19,
            Validators.maxLength(masterValidation.u19!.maxLength),
          ],
          u20: [
            master?.u20,
            Validators.maxLength(masterValidation.u20!.maxLength),
          ],
          u2: [localISO],
        });
      }),
      shareReplayRefCount()
    );

    this.subs.sink = this.fgMaster$
      .pipe(
        filter((fg) => !!fg),
        switchMap((fg) => fg.valueChanges.pipe(withLatestFrom(of(fg))))
      )
      .subscribe(([vc, fg]) => {
        if (fg.valid) {
          const model = vc as UFieldFormModel;
          const u2Dte = model.u2 ? new Date(model.u2) : undefined;
          const master = {
            u1: model.u1 ? model.u1 : undefined,
            u2: u2Dte,
            u3: model.u3 ? model.u3 : undefined,
            u4: model.u4 ? model.u4 : undefined,
            u5: model.u5 ? model.u5 : undefined,
            u6: model.u6 ? model.u6 : undefined,
            u7: model.u7 ? model.u7 : undefined,
            u8: model.u8 ? model.u8 : undefined,
            u9: model.u9 ? model.u9 : undefined,
            u10: model.u10 ? model.u10 : undefined,
            u11: model.u11 ? model.u11 : undefined,
            u12: model.u12 ? model.u12 : undefined,
            u13: model.u13 ? model.u13 : undefined,
            u14: model.u14 ? model.u14 : undefined,
            u15: model.u15 ? model.u15 : undefined,
            u16: model.u16 ? model.u16 : undefined,
            u17: model.u17 ? model.u17 : undefined,
            u18: model.u18 ? model.u18 : undefined,
            u19: model.u19 ? model.u19 : undefined,
            u20: model.u20 ? model.u20 : undefined,
          };
          this.workSvc.updateMasterContact(master);
        }
      });

    this.fgQField$ = this.workSvc.answers$.pipe(
      take(1),
      map((answers) => {
        let tmp = answers?.slice(1).reduce(
          (prev, curr, i) => ({
            ...prev,
            [(i + 1).toString()]: this.fb.control(
              JSON.stringify(curr),
              jsonValidator
            ),
          }),
          { qField: this.fb.control('1') } as { [key: string]: any }
        );
        return this.fb.group(tmp!);
      }),
      shareReplayRefCount()
    );

    this.subs.sink = this.fgQField$
      .pipe(
        switchMap((fg) =>
          fg.valueChanges.pipe(
            map((model) => ({ fg, model: model as QFieldFormModel }))
          )
        ),
        //tap(({ fg, model }) => {
        //  //fg.updateValueAndValidity();
        //  console.log({ status: fg.status, valid: fg.valid });
        //}),
        filter(({ fg, model }) => fg.valid)
      )
      .subscribe(({ fg, model }) => {
        const { qField, ...ansArr } = model;
        try {
          const answers = [
            void 0,
            ...Object.entries(ansArr)
              .sort((a, b) => +a[0] - +b[0])
              .map(([key, value]) =>
                value ? JSON.parse(value!) : (undefined as AnswerType)
              ),
          ] as Answers;
          this.workSvc.updateAnswers(answers);
        } catch (error: any) {
          this.toastr.error(error.message, 'QField Formatting');
        }
      });
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this.workSvc.action$.pipe(map((action) => ({ action }))),
      this.workSvc.answers$.pipe(
        map((answers) => ({
          answers: answers
            ?.slice(1)
            .reduce((prev, curr, i) => ({ ...prev, [`q${i + 1}`]: curr }), {}),
        }))
      ),
      this.workSvc.master$.pipe(map((master) => ({ master }))),
      this.fgQField$.pipe(
        switchMap((fg) => valuesOf<number>(fg.get('qField')!)),
        map((qField) => ({ qField: qField ? +qField : 0 }))
      ),
      of([...Array(300).keys()].map((i) => i + 1)).pipe(
        map((questions) => ({ questions }))
      ),
      this.workSvc.variables$.pipe(map((variables) => ({ variables })))
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.initViewModel();
  }
}

interface ViewModel {
  action: Action;
  answers: { [qField: string]: AnswerType };
  master: Master;
  qField: number | null;
  questions: number[];
  variables: Variables;
}

interface UFieldFormModel {
  u1: string | null;
  u10: string | null;
  u11: string | null;
  u12: string | null;
  u13: string | null;
  u14: string | null;
  u15: string | null;
  u16: string | null;
  u17: string | null;
  u18: string | null;
  u19: string | null;
  u2: string | null;
  u20: string | null;
  u3: string | null;
  u4: string | null;
  u5: string | null;
  u6: string | null;
  u7: string | null;
  u8: string | null;
  u9: string | null;
}

interface QFieldFormModel {
  [q: number]: string | null;
  qField: number;
}

const jsonValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  try {
    if (!control.value) return null;
    const test = JSON.parse(control.value);
    return null;
  } catch (error) {
    return { json: { value: control.value } };
  }
};
